// Copyright ©️ 2026 eVolve MEP, LLC

/* oxlint-disable no-console */

const getStorageArea = () => (localStorage.getItem('STAY_SIGNED_IN') === 'true' ? localStorage : sessionStorage);

/**
 * Removes the item assigned to `key` from storage.
 */
export const removeLocalStorage = <K extends string>(key: K, throwOnError = false) => {
  try {
    getStorageArea().removeItem(key);
  } catch (error) {
    console.error(`Error [removeLocalStorage], ${String(error)}`);
    if (throwOnError) throw error;
  }
};

/**
 * `JSON.stringify`s the value before storing it to guarantee stability of later reads.
 *
 * If `localStorage.STAY_SIGNED_IN` is true, uses `localStorage`. Otherwise, uses `sessionStorage`.
 *
 * @remarks Passing in `undefined` for `value` will remove the local storage item.
 */
export const setLocalStorage = <K extends string, V>(key: K, value: V, throwOnError = false) => {
  try {
    // null is valid, undefined is not
    if (value !== undefined) {
      getStorageArea().setItem(key, JSON.stringify(value));
    } else {
      removeLocalStorage(key);
    }
  } catch (error) {
    console.error(`Error [setLocalStorage], ${String(error)}`);
    if (throwOnError) throw error;
  }
};

/**
 * Used in conjunction with `setLocalStorage`. Parses the stored JSON value before returning.
 *
 * If `localStorage.STAY_SIGNED_IN` is true, uses `localStorage`. Otherwise, uses `sessionStorage`.
 */
export const getLocalStorage = <K extends string, V>(key: K, initialValue?: V): V | undefined => {
  try {
    const item = getStorageArea().getItem(key);
    if (item === 'undefined') return initialValue;
    return item ? (JSON.parse(item) as V) : initialValue;
  } catch (error) {
    console.error(`Error [getLocalStorage], ${String(error)}`);
    return initialValue;
  }
};
